// src/AuthContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "./utils/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

/* ===========================
   Robust API base resolution
   =========================== */
// CRA, Vite, or window-injected globals; fallback to same-origin
const RAW_BASE =
  (import.meta?.env?.VITE_API_BASE_URL ||
    process.env?.VITE_API_BASE_URL ||
    process.env?.REACT_APP_API_BASE_URL ||
    process.env?.REACT_APP_API_BASE ||
    (typeof window !== "undefined" ? window.API_BASE_URL : "") ||
    "").toString();

const BASE_URL = RAW_BASE.trim().replace(/\/$/, "");

/* ===========================
   Config (tweak to taste)
   =========================== */
const REVALIDATE_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const LOGIN_REDIRECT_DEFAULT = "/admin";
const LOGOUT_REDIRECT_DEFAULT = "/login";

export const AuthProvider = ({
  children,
  autoRedirectOnAuth = true,
  loginRedirect = LOGIN_REDIRECT_DEFAULT,
  logoutRedirect = LOGOUT_REDIRECT_DEFAULT,
}) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const abortRef = useRef(null);
  const lastCheckRef = useRef(0);
  const checkingRef = useRef(false);

  // Avoid redundant navigations/loops
  const safelyNavigate = useCallback(
    (to) => {
      if (!to) return;
      if (location.pathname !== to) navigate(to);
    },
    [location.pathname, navigate]
  );

  const _doCheck = useCallback(
    async ({ silent = false } = {}) => {
      if (checkingRef.current) return; // throttle overlapping calls
      checkingRef.current = true;
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        // Your api wrapper should send credentials (cookies) by default
        const res = await api.auth.me({ signal: controller.signal });
        const nextUser = res?.user || null;
        setUser(nextUser);

        // Smart redirect behavior
        if (autoRedirectOnAuth) {
          const isOnAdmin =
            location.pathname === "/admin" || location.pathname.startsWith("/admin/");
          const isOnLoginFlow =
            location.pathname === "/login" ||
            location.pathname.startsWith("/login") ||
            location.pathname === "/register";

          // If logged in and not already on admin, prefer "from" then fallback
          if (nextUser && !isOnAdmin) {
            const from = location.state?.from?.pathname;
            safelyNavigate(from || loginRedirect);
          }

          // If NOT logged in and currently on a protected admin route, send to login
          if (!nextUser && isOnAdmin) {
            safelyNavigate(
              `${logoutRedirect}?returnTo=${encodeURIComponent(location.pathname + location.search)}`
            );
          }

          // If logged in but on login/register, send away
          if (nextUser && isOnLoginFlow) {
            safelyNavigate(loginRedirect);
          }
        }
      } catch (e) {
        if (e?.name === "AbortError") return;
        setUser(null);
        if (!silent) setError(e);
      } finally {
        lastCheckRef.current = Date.now();
        checkingRef.current = false;
        if (!silent) setLoading(false);
      }
    },
    [autoRedirectOnAuth, location.pathname, location.search, location.state, loginRedirect, logoutRedirect, safelyNavigate]
  );

  const checkAuth = useCallback(() => _doCheck({ silent: false }), [_doCheck]);

  // Initial mount
  useEffect(() => {
    checkAuth();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revalidate on visibility/focus and at an interval (SWr-like)
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastCheckRef.current > REVALIDATE_INTERVAL_MS) {
        _doCheck({ silent: true });
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);

    const id = setInterval(() => _doCheck({ silent: true }), REVALIDATE_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
      clearInterval(id);
    };
  }, [_doCheck]);

  // Login/logout with optional redirect
  const login = useCallback(
    async ({ email, password, redirectTo } = {}) => {
      setLoading(true);
      setError(null);
      try {
        await api.auth.login(email, password);
        await _doCheck({ silent: false });

        // Where to go after login
        const url = new URL(window.location.href);
        const fromQ = url.searchParams.get("returnTo");
        const fromState = location.state?.from?.pathname;
        const target = redirectTo || fromQ || fromState || loginRedirect;
        if (autoRedirectOnAuth) safelyNavigate(target);
        return { ok: true };
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [autoRedirectOnAuth, loginRedirect, location.state, safelyNavigate, _doCheck]
  );

  const logout = useCallback(
    async ({ redirectTo } = {}) => {
      setLoading(true);
      setError(null);
      try {
        await api.auth.logout();
        setUser(null);
        const target = redirectTo || logoutRedirect;
        safelyNavigate(target);
        return { ok: true };
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [logoutRedirect, safelyNavigate]
  );

  const revalidate = useCallback(() => _doCheck({ silent: false }), [_doCheck]);

  const value = useMemo(
    () => ({
      baseurl: BASE_URL,
      user,
      isAuthenticated: !!user,
      loading,
      error,
      login,
      logout,
      revalidate,
      checkAuth, // alias
    }),
    [user, loading, error, login, logout, revalidate]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/* ===========================
   Optional: AuthGate
   Wrap protected sections/pages.
   Usage:
     <AuthGate fallback={<Spinner/>}>
       <AdminApp />
     </AuthGate>
   =========================== */
export function AuthGate({ children, fallback = null }) {
  const { loading, isAuthenticated } = useAuth();
  if (loading) return fallback;
  if (!isAuthenticated) return null; // parent route should show a login screen
  return children;
}
